import { useState } from "react";
import { CURRENCY_MAP } from "../../../../constants";

const DashboardTab = ({ event }) => {
  const [dashboard, setDashboard] = useState(event.dashboard);

  const currency = dashboard?.kpis?.revenue?.currency || "";
  const symbol = CURRENCY_MAP[currency] || "";

  if (!dashboard) return null;

  const kpis = dashboard.kpis || {};
  const salesTrend = dashboard.sales_trend || {};
  const genderBreakdown = dashboard.gender_breakdown || {};
  const recentOrders = dashboard.recent_orders || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Tickets Sold */}
        <KpiCard
          label="Tickets Sold"
          value={
            <span className="flex items-baseline gap-1.5">
              {kpis.tickets_sold?.value ?? "—"}
              <span className="text-lg text-slate-500 font-normal">
                / {kpis.tickets_sold?.total ?? "—"}
              </span>
            </span>
          }
          delta={kpis.tickets_sold?.delta_pct}
          deltaLabel={kpis.tickets_sold?.delta_label}
        >
          {/* Today */}
          <div className="mt-3 pt-3 border-t border-white/5">
            <p className="text-xs text-slate-500 mb-1">Today Sales</p>
            <p className="text-sm text-slate-300">
              <span className="text-white font-medium">
                {kpis.tickets_sold?.today_tickets ?? "—"}
              </span>{" "}
              tickets
              {" / "}
              <span className="text-white font-medium">
                {kpis.tickets_sold?.today_orders ?? "—"}
              </span>{" "}
              orders
              {" / "}
              <span className="text-white font-medium">
                {`${symbol}${parseFloat(kpis.tickets_sold?.today_sales_value || 0).toFixed(2)}` ??
                "—"}
              </span>{" "}
            </p>
          </div>

          {/* Order breakdown */}
          <div className="mt-3 grid grid-cols-3 gap-1">
            <div className="bg-slate-800/60 rounded-lg px-2 py-1.5 text-center">
              <p className="text-xs text-slate-500 mb-0.5">Started</p>
              <p className="text-sm font-medium text-white">
                {kpis.tickets_sold?.orders_started ?? "—"}
              </p>
            </div>
            <div className="bg-emerald-500/10 rounded-lg py-1.5 text-center">
              <p className="text-xs text-emerald-600 mb-0.5">Completed</p>
              <p className="text-sm font-medium text-emerald-400">
                {kpis.tickets_sold?.orders_completed ?? "—"}
                <span className="text-xs font-normal ml-0.5">
                  ({kpis.tickets_sold?.orders_completed_pct ?? 0}%)
                </span>
              </p>
            </div>
            <div className="bg-red-500/10 rounded-lg px-2 py-1.5 text-center">
              <p className="text-xs text-red-600 mb-0.5">Expired</p>
              <p className="text-sm font-medium text-red-400">
                {kpis.tickets_sold?.orders_expired ?? "—"}
                <span className="text-xs font-normal ml-0.5">
                  ({kpis.tickets_sold?.orders_expired_pct ?? 0}%)
                </span>
              </p>
            </div>
          </div>
        </KpiCard>

        {/* Revenue */}
        <KpiCard
          label="Revenue"
          value={
            <span className="flex items-baseline gap-1.5">
              {`${symbol}${parseFloat(kpis.revenue?.total || 0).toFixed(2)}` ??
                "—"}
              <div className="relative group inline-block">
                <span className="text-lg text-slate-500 font-normal cursor-help">
                  /{" "}
                  {`${symbol}${parseFloat(kpis.revenue?.predicted_total || 0).toFixed(2)}` ??
                    "—"}
                </span>
                <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-slate-800 border border-white/10 text-xs text-slate-300 rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition pointer-events-none text-center">
                  Predicted total revenue, based on the availability added
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                </div>
              </div>
            </span>
          }
          delta={kpis.revenue?.delta_pct}
          deltaLabel={kpis.revenue?.delta_label}
        >
          <div>
            <div className="flex gap-4 mt-2">
              <div>
                <p className="text-xs text-slate-500">Tickets</p>
                <p className="text-sm text-slate-300">
                  {symbol}
                  {parseFloat(kpis.revenue?.ticket || 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Commission</p>
                <p className="text-sm text-slate-300">
                  {symbol}
                  {parseFloat(
                    kpis.revenue?.commission_without_vat || 0,
                  ).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">VAT</p>
                <p className="text-sm text-slate-300">
                  {symbol}
                  {parseFloat(kpis.revenue?.commission_vat || 0).toFixed(2)}
                </p>
              </div>
            </div>
            <div className="flex gap-4 mt-2">
              <div>
                <p className="text-xs text-slate-500">Commission Total</p>
                <p className="text-sm text-slate-300">
                  {symbol}
                  {parseFloat(kpis.revenue?.commission_total || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </KpiCard>

        {/* Check-ins */}
        <KpiCard
          label="Check-ins"
          value={kpis.check_ins?.value ?? "—"}
          subLabel={
            kpis.check_ins?.attendance_pct != null
              ? `${kpis.check_ins.attendance_pct}% attendance`
              : null
          }
        />

        {/* Refunds */}
        <KpiCard
          label="Refunds"
          value={kpis.refunds?.value ?? "N/A"}
          subLabel={kpis.refunds?.label}
          muted={!kpis.refunds?.value}
        />
      </div>

      {/* ── Sales Trend + Gender ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales Trend */}
        <div className="lg:col-span-2 bg-slate-900/60 border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="text-sm font-semibold text-white">Sales Trend</h3>
              {/* <p className="text-xs text-slate-500 mt-0.5">
                Ticket purchases in the last {salesTrend.window_hours || 24} hours
              </p> */}
            </div>
            <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
              Live
            </span>
          </div>

          <SalesTrendChart salesTrend={salesTrend} />
        </div>

        {/* Gender Breakdown */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white">Audience Gender</h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-5">
            Distribution of paid orders
          </p>

          <GenderChart breakdown={genderBreakdown} />
        </div>
      </div>

      {/* ── Recent Orders ── */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white">Recent Orders</h3>
          <p className="text-xs text-slate-500 mt-0.5">Latest 5 paid orders</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-3 text-left">Buyer</th>
                <th className="px-6 py-3 text-left">Ticket</th>
                <th className="px-6 py-3 text-center">QTY</th>
                <th className="px-6 py-3 text-center">Total</th>
                <th className="px-6 py-3 text-left">Order #</th>
                <th className="px-6 py-3 text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((order, idx) => (
                <tr
                  key={idx}
                  className="border-b border-white/5 last:border-none hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-3 text-sm font-medium text-white">
                    {order.buyer_name || "—"}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-300">
                    {order.ticket_type_name || "—"}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-300 text-center">
                    {order.total_quantity ?? "—"}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-300 text-center">
                    {symbol}
                    {parseFloat(order.total || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-400 font-mono">
                    {order.order_number || "—"}
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500 text-right">
                    {formatDate(order.created_at) || "—"}
                  </td>
                </tr>
              ))}

              {recentOrders.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-10 text-center text-slate-400 text-sm"
                  >
                    No recent orders.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ── KPI Card ── */
const KpiCard = ({
  label,
  value,
  delta,
  deltaLabel,
  subLabel,
  muted,
  children,
}) => {
  const isPositive = delta > 0;

  return (
    <div className="bg-slate-900/60 border border-white/10 rounded-xl py-5 px-2">
      <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">
        {label}
      </p>
      <div
        className={`text-2xl font-semibold ${muted ? "text-slate-500" : "text-white"}`}
      >
        {value}
      </div>

      {delta != null && (
        <p
          className={`text-xs mt-1 font-medium ${isPositive ? "text-emerald-400" : "text-red-400"}`}
        >
          {isPositive ? "+" : ""}
          {delta}% {deltaLabel || ""}
        </p>
      )}

      {subLabel && <p className="text-xs text-slate-500 mt-1">{subLabel}</p>}

      {children}
    </div>
  );
};

/* ── Sales Trend Chart (pure CSS/SVG, no library) ── */
// const SalesTrendChart = ({ salesTrend }) => {
//   const buckets = salesTrend.buckets || [];
//   const ticketTypes = salesTrend.ticket_types || [];

//   const COLORS = ["#a78bfa", "#f472b6", "#fb923c", "#34d399", "#60a5fa", "#facc15"];

//   if (!buckets.length) {
//     return (
//       <div className="h-48 flex items-center justify-center text-slate-500 text-sm">
//         No sales data available
//       </div>
//     );
//   }

//   const hours = buckets.map((b) => b.hour);

//   // Get max value across all ticket types
//   const allCounts = buckets.flatMap((b) =>
//     ticketTypes.map((tt) => b.counts?.[tt.name] || 0)
//   );
//   const maxVal = Math.max(...allCounts, 1);

//   const W = 560;
//   const H = 160;
//   const padL = 24;
//   const padR = 12;
//   const padT = 12;
//   const padB = 28;
//   const chartW = W - padL - padR;
//   const chartH = H - padT - padB;

//   const xPos = (i) => padL + (i / (buckets.length - 1 || 1)) * chartW;
//   const yPos = (val) => padT + chartH - (val / maxVal) * chartH;

//   const gridLines = [0, 1, 2, 3].map((v) => ({
//     y: yPos((v / 3) * maxVal),
//     label: Math.round((v / 3) * maxVal),
//   }));

//   const labelStep = Math.max(1, Math.floor(buckets.length / 6));

//   return (
//     <div>
//       <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 180 }}>
//         {/* Grid lines */}
//         {gridLines.map((g, i) => (
//           <g key={i}>
//             <line
//               x1={padL} y1={g.y} x2={W - padR} y2={g.y}
//               stroke="rgba(255,255,255,0.06)" strokeWidth="1"
//             />
//             <text x={padL - 4} y={g.y + 4} textAnchor="end" fontSize="10" fill="#475569">
//               {g.label}
//             </text>
//           </g>
//         ))}

//         {/* X axis labels */}
//         {buckets.map((b, i) => {
//           if (i % labelStep !== 0 && i !== buckets.length - 1) return null;
//           return (
//             <text key={i} x={xPos(i)} y={H - 4} textAnchor="middle" fontSize="10" fill="#475569">
//               {b.hour}
//             </text>
//           );
//         })}

//         {/* Lines per ticket type */}
//         {ticketTypes.map((tt, ti) => {
//           const points = buckets
//             .map((b, i) => `${xPos(i)},${yPos(b.counts?.[tt.name] || 0)}`)
//             .join(" ");

//           return (
//             <g key={tt.name}>
//               <polyline
//                 points={points}
//                 fill="none"
//                 stroke={COLORS[ti % COLORS.length]}
//                 strokeWidth="1.5"
//                 strokeLinejoin="round"
//                 strokeLinecap="round"
//               />
//               {buckets.map((b, i) => {
//                 const val = b.counts?.[tt.name] || 0;
//                 if (!val) return null;
//                 return (
//                   <circle
//                     key={i}
//                     cx={xPos(i)}
//                     cy={yPos(val)}
//                     r="3"
//                     fill={COLORS[ti % COLORS.length]}
//                   />
//                 );
//               })}
//             </g>
//           );
//         })}
//       </svg>

//       {/* Legend */}
//       <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
//         {ticketTypes.map((tt, ti) => (
//           <div key={tt.name} className="flex items-center gap-1.5">
//             <span
//               className="inline-block w-3 h-0.5 rounded"
//               style={{ backgroundColor: COLORS[ti % COLORS.length] }}
//             />
//             <span className="text-xs text-slate-400">{tt.name}</span>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

const SalesTrendChart = ({ salesTrend }) => {
  const [window, setWindow] = useState("24h");

  const allBuckets = salesTrend.buckets || [];
  const ticketTypes = salesTrend.ticket_types || [];

  const COLORS = [
    "#a78bfa",
    "#f472b6",
    "#fb923c",
    "#34d399",
    "#60a5fa",
    "#facc15",
  ];

  const WINDOWS = [
    {
      key: "24h",
      label: "24h",
      hours: 24,
      labelEvery: 2,
      format: (d) =>
        d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    },
    {
      key: "7d",
      label: "7 days",
      hours: 168,
      labelEvery: 3,
      format: (d) =>
        d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    },
    {
      key: "30d",
      label: "30 days",
      hours: 720,
      labelEvery: 3,
      format: (d) =>
        d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    },
  ];

  const activeWindow = WINDOWS.find((w) => w.key === window);

  // ── Filter + label buckets based on selected window ──
  const now = Date.now();
  const cutoff = now - activeWindow.hours * 60 * 60 * 1000;

  const buckets = allBuckets
    .filter((b) => {
      const ts = new Date(b.hour).getTime();
      return !isNaN(ts) ? ts >= cutoff : true;
    })
    .map((b) => ({
      ...b,
      label: (() => {
        const d = new Date(b.hour);
        return isNaN(d.getTime()) ? b.hour : activeWindow.format(d);
      })(),
    }));

  const allCounts = buckets.flatMap((b) =>
    ticketTypes.map((tt) => b.counts?.[tt.name] || 0),
  );
  const maxVal = Math.max(...allCounts, 1);

  const W = 560;
  const H = 180;
  const padL = 28;
  const padR = 12;
  const padT = 12;
  const padB = 36;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const xPos = (i) => padL + (i / (buckets.length - 1 || 1)) * chartW;
  const yPos = (val) => padT + chartH - (val / maxVal) * chartH;

  const gridLines = [0, 1, 2, 3].map((v) => ({
    y: yPos((v / 3) * maxVal),
    label: Math.round((v / 3) * maxVal),
  }));

  return (
    <div>
      {/* ── Window toggle ── */}
      <div className="flex items-center gap-1 mb-4">
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => setWindow(w.key)}
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              window === w.key
                ? "bg-pink-600 text-white"
                : "text-slate-400 hover:text-white hover:bg-slate-800 border border-white/10"
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {!buckets.length ? (
        <div className="h-48 flex items-center justify-center text-slate-500 text-sm">
          No sales data available
        </div>
      ) : (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: 200 }}
        >
          {/* Horizontal grid lines */}
          {gridLines.map((g, i) => (
            <g key={i}>
              <line
                x1={padL}
                y1={g.y}
                x2={W - padR}
                y2={g.y}
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
              />
              <text
                x={padL - 6}
                y={g.y + 4}
                textAnchor="end"
                fontSize="10"
                fill="#475569"
              >
                {g.label}
              </text>
            </g>
          ))}

          {/* X axis baseline */}
          <line
            x1={padL}
            y1={padT + chartH}
            x2={W - padR}
            y2={padT + chartH}
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="1"
          />

          {/* X axis ticks + labels */}
          {buckets.map((b, i) => {
            if (i % activeWindow.labelEvery !== 0) return null;
            const x = xPos(i);
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1={padT + chartH}
                  x2={x}
                  y2={padT + chartH + 4}
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="1"
                />
                <text
                  x={x}
                  y={padT + chartH + 14}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#475569"
                >
                  {b.label}
                </text>
              </g>
            );
          })}

          {/* Lines + dots per ticket type */}
          {ticketTypes.map((tt, ti) => {
            const points = buckets
              .map((b, i) => `${xPos(i)},${yPos(b.counts?.[tt.name] || 0)}`)
              .join(" ");

            return (
              <g key={tt.name}>
                <polyline
                  points={points}
                  fill="none"
                  stroke={COLORS[ti % COLORS.length]}
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {buckets.map((b, i) => {
                  const val = b.counts?.[tt.name] || 0;
                  if (!val) return null;
                  return (
                    <circle
                      key={i}
                      cx={xPos(i)}
                      cy={yPos(val)}
                      r="3"
                      fill={COLORS[ti % COLORS.length]}
                    />
                  );
                })}
              </g>
            );
          })}
        </svg>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
        {ticketTypes.map((tt, ti) => (
          <div key={tt.name} className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-0.5 rounded"
              style={{ backgroundColor: COLORS[ti % COLORS.length] }}
            />
            <span className="text-xs text-slate-400">{tt.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Gender Donut Chart (SVG) ── */
const GenderChart = ({ breakdown }) => {
  const total = breakdown.total || 0;
  const segments = breakdown.segments || [];

  const COLORS = ["#1e293b", "#94a3b8", "#f472b6", "#a78bfa"];

  if (!total) {
    return (
      <div className="h-40 flex items-center justify-center text-slate-500 text-sm">
        No data
      </div>
    );
  }

  const R = 60;
  const cx = 80;
  const cy = 80;
  const strokeW = 20;

  let cumulative = 0;
  const arcs = segments.map((seg, i) => {
    const pct = seg.count / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;

    const x1 = cx + R * Math.cos(startAngle);
    const y1 = cy + R * Math.sin(startAngle);
    const x2 = cx + R * Math.cos(endAngle);
    const y2 = cy + R * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;

    return {
      d: `M ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2}`,
      color: COLORS[i % COLORS.length],
      seg,
    };
  });

  return (
    <div className="flex flex-col items-center gap-4">
      <svg viewBox="0 0 160 160" className="w-40 h-40">
        {arcs.map((arc, i) => (
          <path
            key={i}
            d={arc.d}
            fill="none"
            stroke={arc.color}
            strokeWidth={strokeW}
            strokeLinecap="butt"
          />
        ))}
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          fontSize="22"
          fontWeight="600"
          fill="#f1f5f9"
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 10}
          textAnchor="middle"
          fontSize="10"
          fill="#64748b"
        >
          ATTENDEES
        </text>
      </svg>

      <div className="w-full space-y-2">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full inline-block"
                style={{ backgroundColor: COLORS[i % COLORS.length] }}
              />
              <span className="text-sm text-slate-400 capitalize">
                {seg.gender || "Unspecified"}
              </span>
            </div>
            <span className="text-sm text-slate-300">
              {seg.count} ({seg.percentage}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardTab;
